import React, { useState, useCallback, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  DialogContentText,
  Stack,
} from '@mui/material';

import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Crop as CropIcon,
  Warning as WarningIcon,
  CameraAlt as CameraIcon,
  PhotoCamera as PhotoCameraIcon,
  FlipCameraAndroid as FlipCameraIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { Crop as CropType, PixelCrop } from 'react-image-crop';
import { uploadAPI, looksAPI } from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUI } from '../contexts/UIContext';

interface DetectedItem {
  temp_id: string;
  item_number?: number;
  s3_key: string;
  image_url: string;
  type?: string;
  category?: string;
  subcategory?: string;
  name?: string;
  description?: string;  // AI-generated brief description
  notes?: string;  // User-editable notes
  colors?: string[];
  patterns?: string[];
  materials?: string[];
  fit?: string;
  style?: string;
  brand?: string;
  size?: string;
  occasions?: string[];
  seasons?: string[];
  features?: string[];
  care_instructions?: string;
  manually_added?: boolean;
  [key: string]: any;
}

interface DetectedPerson {
  person_id: number;
  description: string;
  position: string;
  confidence: number;
}

interface AnalysisResponse {
  detected_items: DetectedItem[];
  total_items: number;
  s3_key: string;
  image_url: string;
  overall_style?: string;
  confidence_score?: number;
  person_count?: number;
  focal_person_clear?: boolean;
  people_detected?: DetectedPerson[];
  requires_person_selection?: boolean;
}

interface DuplicateInfo {
  item_id: number;
  item: any;
  similarity_score: number;
  embedding_similarity: number;
  metadata_similarity: number;
  confidence_level: 'high' | 'medium' | 'low';
  matching_attributes: string[];
}

interface DuplicateCheckResult {
  duplicates: { [temp_id: string]: DuplicateInfo[] };
  summary: {
    total_items_checked: number;
    items_with_potential_duplicates: number;
    high_confidence_duplicates: number;
    medium_confidence_duplicates: number;
    low_confidence_duplicates: number;
  };
}

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useUI();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track which items are selected and their edited data
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [editedItems, setEditedItems] = useState<Map<string, DetectedItem>>(new Map());

  // Person selection state
  const [personSelectionModalOpen, setPersonSelectionModalOpen] = useState(false);
  const [peopleDetectionData, setPeopleDetectionData] = useState<any>(null);
  const [, setSelectedPersonId] = useState<number | null>(null);
  const [personImageDimensions, setPersonImageDimensions] = useState<{width: number, height: number} | null>(null);
  const personDetectionImgRef = useRef<HTMLImageElement | null>(null);

  // Cropping state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [crop, setCrop] = useState<CropType>({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [cropLoading, setCropLoading] = useState(false);

  // Camera state
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Duplicate detection state
  const [duplicateWarningOpen, setDuplicateWarningOpen] = useState(false);
  const [duplicateCheckResult, setDuplicateCheckResult] = useState<DuplicateCheckResult | null>(null);
  const [itemsToSkip, setItemsToSkip] = useState<Set<string>>(new Set());

  // Save the whole photo as a Look Book entry alongside the individual items
  const [saveAsLook, setSaveAsLook] = useState(true);
  const [lookName, setLookName] = useState('');
  const [lookDetails, setLookDetails] = useState<any>(null);
  const [describingLook, setDescribingLook] = useState(false);

  // Have vision name and describe the outfit for its Look Book entry
  React.useEffect(() => {
    const key = analysisResult?.s3_key;
    setLookDetails(null);
    if (!key || (analysisResult?.detected_items?.length || 0) === 0) return;
    let cancelled = false;
    setDescribingLook(true);
    looksAPI.describe(key)
      .then((res) => { if (!cancelled) setLookDetails(res.data); })
      .catch(() => { /* falls back to the default name */ })
      .finally(() => { if (!cancelled) setDescribingLook(false); });
    return () => { cancelled = true; };
  }, [analysisResult?.s3_key, analysisResult?.detected_items?.length]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      // Reset state when new file is dropped
      setAnalysisResult(null);
      setSelectedItems(new Set());
      setEditedItems(new Map());
      setError(null);
      setSuccess(null);
    }
  }, []);

  // Consume files dropped onto the global drag-and-drop overlay
  React.useEffect(() => {
    const pending = (window as any).__pendingUploadFiles as File[] | undefined;
    if (pending && pending.length > 0) {
      onDrop(pending);
      delete (window as any).__pendingUploadFiles;
    }
    // location.key changes on every navigation (incl. same-path ?source=drop),
    // so a global drag-drop handoff is consumed even when already on /upload.
  }, [location.key, onDrop]);

  // Reset image dimensions when modal opens/closes to force re-render of bounding boxes
  React.useEffect(() => {
    if (personSelectionModalOpen && peopleDetectionData) {
      console.log('🔄 Modal opened, resetting image dimensions to trigger re-render');
      setPersonImageDimensions(null);
    }
  }, [personSelectionModalOpen, peopleDetectionData]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
  });

  const handleAnalyze = async () => {
    if (!file) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData();
      formData.append('image', file);

      // Step 1: Detect people in the image
      const detectionResponse = await uploadAPI.detectPeople(formData);
      const detectionData = detectionResponse.data;

      console.log('People detection result:', detectionData);

      // If exactly 1 person detected, automatically analyze that person with cropping
      if (detectionData.people_detected && detectionData.people_detected.length === 1) {
        console.log('🎯 Exactly 1 person detected! Automatically analyzing with cropping...');
        const person = detectionData.people_detected[0];

        // Analyze the person with bounding box (backend will crop automatically)
        const analysisResponse = await uploadAPI.analyzePerson({
          s3_key: detectionData.s3_key,
          image_url: detectionData.image_url,
          bounding_box: person.bounding_box,
          person_description: 'Person 1'
        });

        const analysis: AnalysisResponse = analysisResponse.data;
        setAnalysisResult(analysis);

        // Select all items by default
        const allItemIds = new Set(analysis.detected_items.map(item => item.temp_id));
        setSelectedItems(allItemIds);

        // Initialize edited items with analysis data
        const initialEdits = new Map();
        analysis.detected_items.forEach(item => {
          initialEdits.set(item.temp_id, { ...item });
        });
        setEditedItems(initialEdits);

        setLoading(false);
        return;
      }

      // If multiple people detected (2+), show selection UI
      if (detectionData.people_detected && detectionData.people_detected.length > 1) {
        console.log('🚨 Multiple people detected! Opening person selection modal...');
        console.log('Detection data being set:', detectionData);
        console.log('People array:', detectionData.people_detected);
        console.log('Person count:', detectionData.person_count);
        console.log('Image URL:', detectionData.image_url);

        setPeopleDetectionData(detectionData);
        setPersonSelectionModalOpen(true);
        setLoading(false);
        return;
      }

      // No people detected - proceed with analysis of full image
      console.log('ℹ️ No people detected, analyzing full image...');
      const analyzeFormData = new FormData();
      analyzeFormData.append('image', file);

      const response = await uploadAPI.analyzeImage(analyzeFormData);
      const analysis: AnalysisResponse = response.data;

      console.log('=== FRONTEND: Received Analysis Response ===');
      console.log('Total items:', analysis.total_items);
      console.log('Detected items length:', analysis.detected_items?.length);
      console.log('Person count:', analysis.person_count);
      console.log('Requires person selection:', analysis.requires_person_selection);
      if (analysis.detected_items) {
        analysis.detected_items.forEach((item, idx) => {
          console.log(`Item ${idx + 1}:`, {
            type: item.type,
            category: item.category,
            name: item.name,
            colors: item.colors,
            materials: item.materials,
            brand: item.brand,
          });
        });
      }
      console.log('==========================================');

      setAnalysisResult(analysis);

      // Check if person selection is required
      if (analysis.requires_person_selection && analysis.people_detected && analysis.people_detected.length > 0) {
        // Show person selection modal
        setPersonSelectionModalOpen(true);
        return;
      }

      // Select all items by default
      const allItemIds = new Set(analysis.detected_items.map(item => item.temp_id));
      setSelectedItems(allItemIds);

      // Initialize edited items with analysis data
      const initialEdits = new Map();
      analysis.detected_items.forEach(item => {
        initialEdits.set(item.temp_id, { ...item });
      });
      setEditedItems(initialEdits);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze image. Please try again.');
      console.error('Error analyzing image:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePersonSelection = async (personId: number) => {
    if (!peopleDetectionData || !file) return;

    const person = peopleDetectionData.people_detected.find((p: any) => p.person_id === personId);
    if (!person) return;

    setLoading(true);
    setError(null);
    setPersonSelectionModalOpen(false);
    setSelectedPersonId(personId);

    try {
      console.log('🎯 Analyzing selected person:', personId);
      console.log('📦 Bounding box:', person.bounding_box);

      // Analyze the person directly with bounding box
      // The backend will crop the image and analyze only that person
      const analysisResponse = await uploadAPI.analyzePerson({
        s3_key: peopleDetectionData.s3_key,
        image_url: peopleDetectionData.image_url,
        bounding_box: person.bounding_box,
        person_description: `Person ${personId}`
      });

      const analysis: AnalysisResponse = analysisResponse.data;
      console.log('✅ Analysis response:', analysis);

      // Update the preview to show the cropped image if available
      if (analysis.image_url) {
        setPreview(analysis.image_url);
      }

      setAnalysisResult(analysis);

      // Select all items by default
      const allItemIds = new Set(analysis.detected_items.map(item => item.temp_id));
      setSelectedItems(allItemIds);

      // Initialize edited items with analysis data
      const initialEdits = new Map();
      analysis.detected_items.forEach(item => {
        initialEdits.set(item.temp_id, { ...item });
      });
      setEditedItems(initialEdits);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to process selected person. Please try again.');
      console.error('❌ Error processing person selection:', err);
    } finally {
      setLoading(false);
    }
  };

  // Camera functions
  const handleOpenCamera = async () => {
    setCameraModalOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Failed to access camera. Please check permissions.');
      setCameraModalOpen(false);
    }
  };

  const handleCloseCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setCameraModalOpen(false);
  };

  const handleSwitchCamera = async () => {
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);

    // Stop current stream
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }

    // Start new stream with different camera
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newFacingMode },
        audio: false
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error('Error switching camera:', err);
      setError('Failed to switch camera.');
    }
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to blob
    canvas.toBlob((blob) => {
      if (!blob) return;

      // Create a File from the blob
      const fileName = `camera-photo-${Date.now()}.jpg`;
      const capturedFile = new File([blob], fileName, { type: 'image/jpeg' });

      // Set the file and preview
      setFile(capturedFile);
      setPreview(canvas.toDataURL('image/jpeg'));

      // Reset state when new photo is captured
      setAnalysisResult(null);
      setSelectedItems(new Set());
      setEditedItems(new Map());
      setError(null);
      setSuccess(null);

      // Close camera modal
      handleCloseCamera();
    }, 'image/jpeg', 0.95);
  };

  const handleOpenCropModal = () => {
    setCropModalOpen(true);
    // Reset crop to default
    setCrop({
      unit: '%',
      x: 25,
      y: 25,
      width: 50,
      height: 50,
    });
    setCompletedCrop(null);
  };

  const handleCloseCropModal = () => {
    setCropModalOpen(false);
    setCompletedCrop(null);
  };

  const getCroppedImg = async (): Promise<string | null> => {
    if (!completedCrop || !imgRef.current) return null;

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return null;

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          resolve(null);
          return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
      }, 'image/jpeg', 0.95);
    });
  };

  const handleAnalyzeCrop = async () => {
    if (!completedCrop || !analysisResult) return;

    setCropLoading(true);
    setError(null);

    try {
      const croppedImageData = await getCroppedImg();
      if (!croppedImageData) {
        setError('Failed to process cropped image');
        return;
      }

      // Send cropped image for analysis
      const response = await uploadAPI.analyzeCrop({
        image_data: croppedImageData,
        s3_key: analysisResult.s3_key,
        image_url: analysisResult.image_url,
      });

      const newItem: DetectedItem = response.data.detected_item;

      // Add to detected items
      const updatedAnalysis = {
        ...analysisResult,
        detected_items: [...analysisResult.detected_items, newItem],
        total_items: analysisResult.total_items + 1,
      };

      setAnalysisResult(updatedAnalysis);

      // Add to selected items and edited items
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.add(newItem.temp_id);
        return newSet;
      });
      setEditedItems(prev => new Map(prev).set(newItem.temp_id, { ...newItem }));

      setSuccess('Successfully added manual item!');
      handleCloseCropModal();

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to analyze cropped region. Please try again.');
      console.error('Error analyzing crop:', err);
    } finally {
      setCropLoading(false);
    }
  };

  const handleToggleItem = (tempId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(tempId)) {
      newSelected.delete(tempId);
    } else {
      newSelected.add(tempId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (!analysisResult) return;
    const allItemIds = new Set(analysisResult.detected_items.map(item => item.temp_id));
    setSelectedItems(allItemIds);
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
  };

  const handleUpdateItem = (tempId: string, field: string, value: any) => {
    const newEditedItems = new Map(editedItems);
    const item = newEditedItems.get(tempId);
    if (item) {
      const updatedItem = { ...item, [field]: value };
      newEditedItems.set(tempId, updatedItem);
      setEditedItems(newEditedItems);
    }
  };

  const handleSaveSelected = async () => {
    if (!analysisResult || selectedItems.size === 0) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Get selected items with their edits
      const itemsToSave = Array.from(selectedItems)
        .map(tempId => editedItems.get(tempId))
        .filter((item): item is DetectedItem => item !== undefined);

      // Check for duplicates first
      const duplicateCheckResponse = await uploadAPI.checkDuplicates({ items: itemsToSave });
      const duplicateResult: DuplicateCheckResult = duplicateCheckResponse.data;

      console.log('Duplicate check result:', duplicateResult);

      // If duplicates found, show warning dialog
      if (duplicateResult.summary.items_with_potential_duplicates > 0) {
        setDuplicateCheckResult(duplicateResult);
        setDuplicateWarningOpen(true);
        setLoading(false);
        return;
      }

      // No duplicates, proceed with saving
      await proceedWithSaving(itemsToSave);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save items. Please try again.');
      console.error('Error saving items:', err);
      setLoading(false);
    }
  };

  const proceedWithSaving = async (itemsToSave: DetectedItem[]) => {
    try {
      // Filter out skipped items
      const finalItemsToSave = itemsToSave.filter(item => !itemsToSkip.has(item.temp_id));

      if (finalItemsToSave.length === 0) {
        setError('No items to save after filtering duplicates.');
        setLoading(false);
        return;
      }

      const response = await uploadAPI.createItemsBatch({ items: finalItemsToSave });

      const count = response.data.total_created;

      // Keep the outfit itself, not just its pieces: the photo + the items it became.
      // Skipped duplicates point at the wardrobe item the user already owns.
      if (saveAsLook && analysisResult) {
        const createdIds: number[] = (response.data.created_items || []).map((i: any) => i.id);
        const existingIds: number[] = itemsToSave
          .filter(item => itemsToSkip.has(item.temp_id))
          .map(item => duplicateCheckResult?.duplicates[item.temp_id]?.[0]?.item_id)
          .filter((id): id is number => typeof id === 'number');
        try {
          const look = await looksAPI.createLook({
            name: lookName.trim() || lookDetails?.name || defaultLookName(),
            description: lookDetails?.description || null,
            occasion: lookDetails?.occasion || null,
            season: lookDetails?.season || null,
            tags: lookDetails?.tags || [],
            item_ids: Array.from(new Set([...createdIds, ...existingIds])),
            images: [{ url: analysisResult.image_url, s3_key: analysisResult.s3_key }],
          });
          setLoading(false);
          toast(`Added ${count} item${count === 1 ? '' : 's'} and saved the look to your Look Book`, 'success', { duration: 5000 });
          navigate(`/look-book/${look.data.id}`);
          return;
        } catch (lookErr) {
          console.error('Error saving look:', lookErr);
          toast("Items were added, but the look couldn't be saved to Look Book", 'warning', { duration: 6000 });
        }
      }

      setLoading(false);
      // Prominent app-level toast (persists across navigation) + immediate redirect,
      // instead of a small inline banner followed by an awkward delayed jump.
      toast(`Added ${count} item${count === 1 ? '' : 's'} to your wardrobe`, 'success', { duration: 5000 });
      navigate('/wardrobe');

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save items. Please try again.');
      console.error('Error saving items:', err);
      setLoading(false);
    }
  };

  const defaultLookName = () => {
    const style = (analysisResult?.overall_style || '').trim();
    const date = new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return style && style.length <= 40
      ? `${style.charAt(0).toUpperCase()}${style.slice(1)} look`
      : `Look — ${date}`;
  };

  const handleConfirmAddDuplicates = async () => {
    if (!duplicateCheckResult) return;

    setDuplicateWarningOpen(false);
    setLoading(true);

    // Get all items to save
    const itemsToSave = Array.from(selectedItems)
      .map(tempId => editedItems.get(tempId))
      .filter((item): item is DetectedItem => item !== undefined);

    await proceedWithSaving(itemsToSave);
  };

  const handleSkipItem = (tempId: string) => {
    setItemsToSkip(prev => {
      const newSet = new Set(prev);
      newSet.add(tempId);
      return newSet;
    });
  };

  const handleUnskipItem = (tempId: string) => {
    setItemsToSkip(prev => {
      const newSet = new Set(prev);
      newSet.delete(tempId);
      return newSet;
    });
  };

  const handleSkipAllDuplicates = () => {
    if (!duplicateCheckResult) return;
    const duplicateTempIds = Object.keys(duplicateCheckResult.duplicates);
    setItemsToSkip(new Set(duplicateTempIds));
  };

  const renderItemCard = (item: DetectedItem) => {
    const tempId = item.temp_id;
    const isSelected = selectedItems.has(tempId);
    const editedItem = editedItems.get(tempId) || item;

    return (
      <Card
        key={tempId}
        sx={{
          mb: 2,
          border: isSelected ? '2px solid #1976d2' : '1px solid #e0e0e0',
          position: 'relative',
          backgroundColor: editedItem.manually_added ? '#f0f8ff' : 'white',
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Checkbox
              checked={isSelected}
              onChange={() => handleToggleItem(tempId)}
              color="primary"
              sx={{ mt: 1 }}
            />
            <Box sx={{ flex: 1, ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {editedItem.manually_added ? '✋ Manual Item' : `Item ${item.item_number}`}
                </Typography>
                {isSelected && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
              </Box>
              <TextField
                fullWidth
                size="small"
                label="Item Name"
                value={editedItem.name || ''}
                onChange={(e) => handleUpdateItem(tempId, 'name', e.target.value)}
                placeholder={`${editedItem.category || editedItem.type || 'Item'} ${editedItem.brand ? `- ${editedItem.brand}` : ''}`}
                variant="outlined"
                sx={{
                  '& .MuiInputBase-root': {
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }
                }}
              />
            </Box>
          </Box>

          {/* AI-Generated Description - Editable */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Description"
              value={editedItem.description || ''}
              onChange={(e) => handleUpdateItem(tempId, 'description', e.target.value)}
              placeholder="AI-generated description will appear here..."
              helperText="AI-generated description - you can edit this"
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5',
                }
              }}
            />
          </Box>

          {/* User Notes - Always Editable */}
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              size="small"
              label="Notes"
              value={editedItem.notes || ''}
              onChange={(e) => handleUpdateItem(tempId, 'notes', e.target.value)}
              placeholder="Add your own notes about this item..."
              helperText="Personal notes (optional)"
            />
          </Box>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">View/Edit Details</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Category"
                    value={editedItem.category || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'category', e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Brand"
                    value={editedItem.brand || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'brand', e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Size"
                    value={editedItem.size || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'size', e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Type"
                    value={editedItem.type || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'type', e.target.value)}
                    placeholder="e.g., t-shirt, jeans, sneakers"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Subcategory"
                    value={editedItem.subcategory || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'subcategory', e.target.value)}
                    placeholder="e.g., crew-neck t-shirt, skinny jeans"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Style"
                    value={editedItem.style || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'style', e.target.value)}
                    placeholder="e.g., casual, formal, athletic"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Fit"
                    value={editedItem.fit || ''}
                    onChange={(e) => handleUpdateItem(tempId, 'fit', e.target.value)}
                    placeholder="e.g., slim, regular, loose"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Colors"
                    value={editedItem.colors?.join(', ') || ''}
                    onChange={(e) => {
                      const colors = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                      handleUpdateItem(tempId, 'colors', colors);
                    }}
                    placeholder="e.g., navy blue, white, red (comma-separated)"
                    helperText="Separate multiple colors with commas"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Materials"
                    value={editedItem.materials?.join(', ') || ''}
                    onChange={(e) => {
                      const materials = e.target.value.split(',').map(m => m.trim()).filter(m => m);
                      handleUpdateItem(tempId, 'materials', materials);
                    }}
                    placeholder="e.g., cotton, polyester, leather (comma-separated)"
                    helperText="Separate multiple materials with commas"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Patterns"
                    value={editedItem.patterns?.join(', ') || ''}
                    onChange={(e) => {
                      const patterns = e.target.value.split(',').map(p => p.trim()).filter(p => p);
                      handleUpdateItem(tempId, 'patterns', patterns);
                    }}
                    placeholder="e.g., solid, striped, floral (comma-separated)"
                    helperText="Separate multiple patterns with commas"
                  />
                </Grid>

                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Features"
                    value={editedItem.features?.join(', ') || ''}
                    onChange={(e) => {
                      const features = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                      handleUpdateItem(tempId, 'features', features);
                    }}
                    placeholder="e.g., pockets, zipper, buttons (comma-separated)"
                    helperText="Separate multiple features with commas"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Occasions"
                    value={editedItem.occasions?.join(', ') || ''}
                    onChange={(e) => {
                      const occasions = e.target.value.split(',').map(o => o.trim()).filter(o => o);
                      handleUpdateItem(tempId, 'occasions', occasions);
                    }}
                    placeholder="e.g., casual, work, formal (comma-separated)"
                    helperText="Separate with commas"
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Seasons"
                    value={editedItem.seasons?.join(', ') || ''}
                    onChange={(e) => {
                      const seasons = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                      handleUpdateItem(tempId, 'seasons', seasons);
                    }}
                    placeholder="e.g., spring, summer, fall, winter (comma-separated)"
                    helperText="Separate with commas"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    );
  };

  // Simple step indicator driven by state
  const uploadStep = analysisResult ? 2 : file ? 1 : 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box className="fade-in-up" sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
          Step {uploadStep + 1} of 3 · {['Pick an image', 'Analyze', 'Confirm & save'][uploadStep]}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.5 }}>Add to Wardrobe</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
          Upload or snap a photo. We'll detect every item and let you pick which ones to keep before anything is saved.
        </Typography>
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 2,
            '& > span': {
              height: 3,
              flexGrow: 1,
              backgroundColor: 'rgba(0,0,0,0.08)',
              transition: 'background-color 0.4s ease',
            },
          }}
        >
          <Box component="span" sx={{ backgroundColor: uploadStep >= 0 ? 'secondary.main !important' : undefined }} />
          <Box component="span" sx={{ backgroundColor: uploadStep >= 1 ? 'secondary.main !important' : undefined }} />
          <Box component="span" sx={{ backgroundColor: uploadStep >= 2 ? 'secondary.main !important' : undefined }} />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Upload Section */}
        <Grid size={{ xs: 12, md: analysisResult ? 5 : 12 }}>
          <Paper sx={{ p: 3 }}>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed',
                borderColor: isDragActive ? 'secondary.main' : 'rgba(0,0,0,0.15)',
                p: 3,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? 'rgba(212, 175, 55, 0.08)' : 'transparent',
                minHeight: 220,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                },
              }}
            >
              <input {...getInputProps()} />
              {preview ? (
                <img
                  src={preview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                />
              ) : (
                <>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 56,
                      color: isDragActive ? 'secondary.main' : 'rgba(0,0,0,0.35)',
                      mb: 2,
                      transition: 'color 0.25s ease, transform 0.25s ease',
                      transform: isDragActive ? 'translateY(-4px)' : 'none',
                    }}
                  />
                  <Typography variant="h6" sx={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', mb: 0.5 }}>
                    {isDragActive ? 'Release to upload' : 'Drop your photo'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to choose from your device
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, letterSpacing: '0.05em' }}>
                    JPG · PNG · GIF · WEBP
                  </Typography>
                </>
              )}
            </Box>

            {/* Camera Button */}
            {!file && (
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PhotoCameraIcon />}
                sx={{ mt: 2 }}
                onClick={handleOpenCamera}
              >
                Take Photo
              </Button>
            )}

            {file && !analysisResult && (
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 2 }}
                onClick={handleAnalyze}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Analyze Image'}
              </Button>
            )}

            {analysisResult && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detected {analysisResult.total_items} item(s)
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                  onClick={() => {
                    setAnalysisResult(null);
                    setSelectedItems(new Set());
                    setEditedItems(new Map());
                  }}
                >
                  Upload Different Image
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={handleOpenCropModal}
                  color="secondary"
                >
                  Add Item Manually
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Missed an item? Click here to highlight it in the image.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Detected Items Section */}
        {analysisResult && analysisResult.detected_items.length > 0 && (
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Detected Items ({analysisResult.total_items})
                </Typography>
                <Box>
                  <Button size="small" onClick={handleSelectAll} sx={{ mr: 1 }}>
                    Select All
                  </Button>
                  <Button size="small" onClick={handleDeselectAll}>
                    Deselect All
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ maxHeight: 600, overflowY: 'auto' }}>
                {analysisResult.detected_items.map(item => renderItemCard(item))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={saveAsLook} onChange={(e) => setSaveAsLook(e.target.checked)} />}
                  label="Also save this outfit to Look Book"
                />
                {saveAsLook && (
                  <TextField
                    size="small"
                    label="Look name"
                    placeholder={describingLook ? 'Suggesting a name…' : (lookDetails?.name || defaultLookName())}
                    InputLabelProps={{ shrink: true }}
                    helperText={lookDetails?.name && !lookName ? 'Suggested from your photo — type to change' : undefined}
                    value={lookName}
                    onChange={(e) => setLookName(e.target.value)}
                    sx={{ flex: 1, minWidth: 200 }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {selectedItems.size} item(s) selected
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/wardrobe')}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveSelected}
                    disabled={loading || selectedItems.size === 0}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      `Add ${selectedItems.size} Item(s) to Wardrobe`
                    )}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Messages */}
        {error && (
          <Grid size={12}>
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          </Grid>
        )}

        {success && (
          <Grid size={12}>
            <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert>
          </Grid>
        )}
      </Grid>

      {/* Person Selection Modal */}
      <Dialog
        open={personSelectionModalOpen}
        onClose={() => {
          console.log('🚪 Person selection modal closing');
          setPersonSelectionModalOpen(false);
        }}
        maxWidth="md"
        fullWidth
        onTransitionEnter={() => {
          console.log('🎬 Person selection modal OPENED');
          console.log('   - Modal is now visible');
          console.log('   - peopleDetectionData:', peopleDetectionData);
        }}
      >
        <DialogTitle>
          <Typography variant="h6">Select Person to Analyze</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            We detected {peopleDetectionData?.person_count} people in this image. Click on a person to analyze their clothing.
          </Typography>

          {(() => {
            console.log('📋 DialogContent Rendering:');
            console.log('   - peopleDetectionData exists:', !!peopleDetectionData);
            console.log('   - peopleDetectionData value:', peopleDetectionData);
            return null;
          })()}

          {peopleDetectionData && (
            <Box sx={{ position: 'relative', display: 'inline-block', maxWidth: '100%', width: '100%' }}>
              <img
                ref={personDetectionImgRef}
                src={peopleDetectionData.image_url}
                alt="Multiple people detected"
                style={{
                  width: '100%',
                  height: 'auto',
                  display: 'block',
                  borderRadius: 8
                }}
                id="person-detection-image"
                crossOrigin="anonymous"
                onLoad={(e) => {
                  const img = e.target as HTMLImageElement;
                  const imgWidth = img.offsetWidth;
                  const imgHeight = img.offsetHeight;

                  console.log('🖼️ Person Detection Image Loaded:');
                  console.log('   - Image natural size:', img.naturalWidth, 'x', img.naturalHeight);
                  console.log('   - Image display size:', imgWidth, 'x', imgHeight);
                  console.log('   - Image URL:', peopleDetectionData.image_url);
                  console.log('   - People detected:', peopleDetectionData.people_detected?.length || 0);

                  // SET IMAGE DIMENSIONS IN STATE TO TRIGGER SVG RENDERING
                  setPersonImageDimensions({ width: imgWidth, height: imgHeight });
                  console.log('   ✅ Image dimensions set in state, SVG will now render!');

                  // Log people detection data
                  if (peopleDetectionData.people_detected) {
                    console.log('👥 People Detected:', peopleDetectionData.people_detected);
                    peopleDetectionData.people_detected.forEach((person: any, idx: number) => {
                      console.log(`   Person ${idx + 1}:`, {
                        person_id: person.person_id,
                        bounding_box: person.bounding_box,
                        confidence: person.confidence
                      });
                    });
                  } else {
                    console.error('   ❌ No people_detected array!');
                  }
                }}
              />
              <style>{`
                .person-bbox:hover .hover-highlight {
                  opacity: 1 !important;
                }
                .person-bbox {
                  transition: all 0.2s ease-in-out;
                }
                .person-bbox:hover {
                  filter: brightness(1.1);
                }
              `}</style>
              {(() => {
                console.log('🎭 CSS styles injected for person-bbox hover effects');
                return null;
              })()}
              <svg
                id="person-detection-svg"
                key={`svg-${peopleDetectionData?.people_detected?.length || 0}-${personImageDimensions?.width || 0}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  zIndex: 1000,
                }}
                onLoad={() => {
                  console.log('🎨 SVG element loaded');
                  const svg = document.getElementById('person-detection-svg');
                  if (svg) {
                    const rect = svg.getBoundingClientRect();
                    console.log('   - SVG position:', { top: rect.top, left: rect.left, width: rect.width, height: rect.height });
                    console.log('   - SVG visible in DOM:', svg.offsetParent !== null);
                  }
                }}
              >
                {(() => {
                  console.log('🎨 Rendering SVG overlays...');
                  console.log('   - peopleDetectionData exists:', !!peopleDetectionData);
                  console.log('   - people_detected exists:', !!peopleDetectionData?.people_detected);
                  console.log('   - people_detected length:', peopleDetectionData?.people_detected?.length || 0);
                  console.log('   - Full peopleDetectionData:', JSON.stringify(peopleDetectionData, null, 2));

                  // Check if SVG is in DOM
                  setTimeout(() => {
                    const svg = document.getElementById('person-detection-svg');
                    console.log('   - SVG element found in DOM:', !!svg);
                    if (svg) {
                      console.log('   - SVG children count:', svg.children.length);
                      console.log('   - SVG innerHTML length:', svg.innerHTML.length);
                    }
                  }, 100);

                  if (!peopleDetectionData?.people_detected || peopleDetectionData.people_detected.length === 0) {
                    console.error('   ❌ No people_detected to render!');
                    return null;
                  }

                  // ONLY RENDER IF WE HAVE IMAGE DIMENSIONS FROM STATE
                  if (!personImageDimensions) {
                    console.log('   ⏳ Waiting for image to load... dimensions not yet available');
                    return null;
                  }

                  console.log('   ✅ Image dimensions available from state:', personImageDimensions);

                  return peopleDetectionData.people_detected.map((person: any) => {
                    const bbox = person.bounding_box;
                    console.log(`   🎯 Rendering person ${person.person_id}:`, bbox);

                    // USE DIMENSIONS FROM STATE INSTEAD OF QUERYING DOM
                    const imgWidth = personImageDimensions.width;
                    const imgHeight = personImageDimensions.height;

                    const x = bbox.left * imgWidth;
                    const y = bbox.top * imgHeight;
                    const width = bbox.width * imgWidth;
                    const height = bbox.height * imgHeight;

                    // Calculate label position - keep it visible within viewport
                    const labelHeight = 35;
                    const minTopMargin = 10; // Minimum distance from top edge
                    const labelY = y - labelHeight < minTopMargin ? y + 5 : y - labelHeight;
                    const labelIsInside = y - labelHeight < minTopMargin;

                    console.log(`   Person ${person.person_id} calculated positions:`, {
                      imgWidth, imgHeight,
                      x, y, width, height,
                      normalized: bbox,
                      labelY, labelIsInside
                    });

                    // Verify coordinates are valid
                    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
                      console.error(`   ❌ Invalid coordinates for person ${person.person_id}!`, { x, y, width, height });
                      return null;
                    }

                    console.log(`   ✅ Creating SVG elements for person ${person.person_id}`);
                    console.log(`      - Outer glow rect: x=${x-4}, y=${y-4}, w=${width+8}, h=${height+8}`);
                    console.log(`      - Main rect: x=${x}, y=${y}, w=${width}, h=${height}`);
                    console.log(`      - Has pointer events: auto`);
                    console.log(`      - Has className: person-bbox`);

                    return (
                      <g
                        key={person.person_id}
                        onClick={() => {
                          console.log(`🖱️ Person ${person.person_id} clicked!`);
                          handlePersonSelection(person.person_id);
                        }}
                        onMouseEnter={() => {
                          console.log(`🖱️ Mouse entered person ${person.person_id} bounding box`);
                        }}
                        onMouseLeave={() => {
                          console.log(`🖱️ Mouse left person ${person.person_id} bounding box`);
                        }}
                        style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                        className="person-bbox"
                      >
                        {(() => {
                          console.log(`      🔷 Rendering <g> for person ${person.person_id}`);
                          return null;
                        })()}
                        {/* Outer glow effect */}
                        <rect
                          x={x - 4}
                          y={y - 4}
                          width={width + 8}
                          height={height + 8}
                          fill="none"
                          stroke="rgba(25, 118, 210, 0.3)"
                          strokeWidth="8"
                          rx="4"
                        />
                        {/* Main bounding box */}
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill="rgba(25, 118, 210, 0.15)"
                          stroke="#1976d2"
                          strokeWidth="4"
                          strokeDasharray="10,5"
                          rx="2"
                        >
                          <animate
                            attributeName="stroke-dashoffset"
                            from="0"
                            to="15"
                            dur="0.5s"
                            repeatCount="indefinite"
                          />
                        </rect>
                        {/* Hover highlight */}
                        <rect
                          x={x}
                          y={y}
                          width={width}
                          height={height}
                          fill="rgba(25, 118, 210, 0.2)"
                          stroke="none"
                          opacity="0"
                          className="hover-highlight"
                          rx="2"
                        />
                        {/* Label background */}
                        <rect
                          x={x}
                          y={labelY}
                          width={120}
                          height={35}
                          fill="#1976d2"
                          rx="4"
                        />
                        {/* Click indicator icon */}
                        <circle
                          cx={x + 15}
                          cy={labelY + 17.5}
                          r="8"
                          fill="white"
                          opacity="0.9"
                        />
                        <text
                          x={x + 15}
                          y={labelY + 22}
                          fill="#1976d2"
                          fontSize="12"
                          fontWeight="bold"
                          textAnchor="middle"
                          style={{ pointerEvents: 'none' }}
                        >
                          &#x1F5B1;
                        </text>
                        {/* Label text */}
                        <text
                          x={x + 30}
                          y={labelY + 21}
                          fill="white"
                          fontSize="16"
                          fontWeight="bold"
                          style={{ pointerEvents: 'none' }}
                        >
                          Person {person.person_id}
                        </text>
                        {/* Pulsing corner indicators */}
                        {(() => {
                          console.log(`      🔶 Adding 4 pulsing orange corners for person ${person.person_id}`);
                          console.log(`         - Top-left: (${x+5}, ${y+5})`);
                          console.log(`         - Top-right: (${x+width-5}, ${y+5})`);
                          console.log(`         - Bottom-left: (${x+5}, ${y+height-5})`);
                          console.log(`         - Bottom-right: (${x+width-5}, ${y+height-5})`);
                          return null;
                        })()}
                        <circle cx={x + 5} cy={y + 5} r="4" fill="#ff9800">
                          <animate attributeName="r" values="4;6;4" dur="1.5s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
                        </circle>
                        <circle cx={x + width - 5} cy={y + 5} r="4" fill="#ff9800">
                          <animate attributeName="r" values="4;6;4" dur="1.5s" begin="0.3s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" begin="0.3s" repeatCount="indefinite" />
                        </circle>
                        <circle cx={x + 5} cy={y + height - 5} r="4" fill="#ff9800">
                          <animate attributeName="r" values="4;6;4" dur="1.5s" begin="0.6s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" begin="0.6s" repeatCount="indefinite" />
                        </circle>
                        <circle cx={x + width - 5} cy={y + height - 5} r="4" fill="#ff9800">
                          <animate attributeName="r" values="4;6;4" dur="1.5s" begin="0.9s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" begin="0.9s" repeatCount="indefinite" />
                        </circle>
                        {(() => {
                          console.log(`      ✅ COMPLETED rendering person ${person.person_id} with all visual effects`);
                          return null;
                        })()}
                      </g>
                    );
                  });
                })()}
                {(() => {
                  console.log('✨ === SVG RENDERING COMPLETE ===');
                  console.log('   Total people rendered:', peopleDetectionData?.people_detected?.length || 0);
                  setTimeout(() => {
                    const svg = document.getElementById('person-detection-svg');
                    if (svg) {
                      console.log('   📊 Final SVG state:');
                      console.log('      - Children count:', svg.children.length);
                      console.log('      - Computed style:', window.getComputedStyle(svg).display);
                      console.log('      - Is visible:', svg.offsetParent !== null);
                      console.log('      - Bounding rect:', svg.getBoundingClientRect());

                      // Log all <g> elements
                      const groups = svg.querySelectorAll('g.person-bbox');
                      console.log('      - <g> elements with class person-bbox:', groups.length);
                      groups.forEach((g, idx) => {
                        const rect = g.getBoundingClientRect();
                        console.log(`         Group ${idx}:`, { width: rect.width, height: rect.height, visible: rect.width > 0 && rect.height > 0 });
                      });

                      // Log all rectangles
                      const rects = svg.querySelectorAll('rect');
                      console.log('      - Total <rect> elements:', rects.length);

                      // Log all circles
                      const circles = svg.querySelectorAll('circle');
                      console.log('      - Total <circle> elements:', circles.length);
                    }
                  }, 500);
                  return null;
                })()}
              </svg>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Click on a person to analyze their clothing.</strong> Each person is outlined with an animated blue border and pulsing orange corners. Hover over them to see the highlight.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPersonSelectionModalOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Crop Modal */}
      <Dialog
        open={cropModalOpen}
        onClose={handleCloseCropModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CropIcon />
            <Typography variant="h6">Select Item to Add</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Draw a box around the item you want to add. This helps us analyze it better and improves future detection.
          </Typography>
          {preview && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={undefined}
            >
              <img
                ref={imgRef}
                src={preview}
                alt="Crop preview"
                style={{ maxWidth: '100%', maxHeight: '500px' }}
              />
            </ReactCrop>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCropModal} disabled={cropLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleAnalyzeCrop}
            variant="contained"
            disabled={!completedCrop || cropLoading}
            startIcon={cropLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {cropLoading ? 'Analyzing...' : 'Analyze Selected Region'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog
        open={duplicateWarningOpen}
        onClose={() => setDuplicateWarningOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            <Typography variant="h6">Potential Duplicates Detected</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            We found {duplicateCheckResult?.summary.items_with_potential_duplicates} item(s) that may already exist in your wardrobe.
            You can choose to skip duplicates or add them anyway.
          </DialogContentText>

          {duplicateCheckResult && Object.entries(duplicateCheckResult.duplicates).map(([tempId, duplicates]) => {
            const newItem = editedItems.get(tempId);
            if (!newItem) return null;

            const isSkipped = itemsToSkip.has(tempId);
            const topDuplicate = duplicates[0];

            return (
              <Card
                key={tempId}
                sx={{
                  mb: 2,
                  border: isSkipped ? '2px solid #ff9800' : '1px solid #e0e0e0',
                  backgroundColor: isSkipped ? '#fff3e0' : 'white',
                }}
              >
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        New Item: {newItem.category || newItem.type || 'Unknown'} {newItem.brand ? `- ${newItem.brand}` : ''}
                      </Typography>
                      <Chip
                        label={`${topDuplicate.confidence_level.toUpperCase()} confidence: ${(topDuplicate.similarity_score * 100).toFixed(0)}% match`}
                        color={
                          topDuplicate.confidence_level === 'high' ? 'error' :
                          topDuplicate.confidence_level === 'medium' ? 'warning' : 'info'
                        }
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>

                    <Divider />

                    <Typography variant="subtitle2" color="text.secondary">
                      Similar to existing item in your wardrobe:
                    </Typography>

                    <List dense>
                      {duplicates.slice(0, 2).map((dup, idx) => (
                        <ListItem
                          key={idx}
                          sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            mb: 1,
                            backgroundColor: '#fafafa',
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              variant="rounded"
                              src={dup.item.thumbnail_url}
                              sx={{ width: 60, height: 60 }}
                            >
                              {dup.item.category?.[0] || '?'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2">
                                {dup.item.category} {dup.item.brand ? `- ${dup.item.brand}` : ''}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" display="block">
                                  Similarity: {(dup.similarity_score * 100).toFixed(0)}%
                                </Typography>
                                {dup.matching_attributes.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Matches: {dup.matching_attributes.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {isSkipped ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleUnskipItem(tempId)}
                        >
                          Add Anyway
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          onClick={() => handleSkipItem(tempId)}
                        >
                          Skip This Item
                        </Button>
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}

          <Alert severity="info" sx={{ mt: 2 }}>
            Tip: Items marked as skipped will not be added to your wardrobe.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setDuplicateWarningOpen(false);
              setItemsToSkip(new Set());
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSkipAllDuplicates}
            color="warning"
            variant="outlined"
          >
            Skip All Duplicates
          </Button>
          <Button
            onClick={handleConfirmAddDuplicates}
            variant="contained"
            color="primary"
          >
            Continue with Selected Items
          </Button>
        </DialogActions>
      </Dialog>

      {/* Camera Modal */}
      <Dialog
        open={cameraModalOpen}
        onClose={handleCloseCamera}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#000' }
        }}
      >
        <DialogTitle sx={{ color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CameraIcon sx={{ mr: 1 }} />
            Take Photo
          </Box>
          <Button
            onClick={handleSwitchCamera}
            startIcon={<FlipCameraIcon />}
            sx={{ color: '#fff' }}
          >
            Switch Camera
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, position: 'relative', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '70vh',
              objectFit: 'contain'
            }}
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#000', p: 2 }}>
          <Button onClick={handleCloseCamera} sx={{ color: '#fff' }}>
            Cancel
          </Button>
          <Button
            onClick={handleCapturePhoto}
            variant="contained"
            startIcon={<PhotoCameraIcon />}
          >
            Capture Photo
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Upload;
